import { FC, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Divider, Tree, Typography } from 'antd';
import type { TreeProps } from 'antd';
import { TbChevronDown } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    buildOAuthScopeTree,
    defaultOAuthScopeCatalog,
    OAuthScopeCategory,
    OAuthScopeDefinition,
} from '@/shared/oauth/scopes';

import './styles.css';

interface OAuthApplicationScopeSelectorProps {
    value?: string[];
    onChange?: (scopes: string[]) => void;
}

const sections: Array<{ category: OAuthScopeCategory; title: string; description: string }> = [
    {
        category: 'account',
        title: 'Account Scopes',
        description: 'Identity and membership permissions for the authorizing user.',
    },
    {
        category: 'workspace',
        title: 'Workspace Scopes',
        description: 'Workspace details, settings, and federated search permissions.',
    },
    {
        category: 'resources',
        title: 'Workspace Resource Scopes',
        description: 'Granular permissions for each supported workspace resource.',
    },
];

const OAuthApplicationScopeSelector: FC<OAuthApplicationScopeSelectorProps> = ({ value = [], onChange }) => {
    const { token } = useAntDesignTheme();
    const [catalog, setCatalog] = useState<OAuthScopeDefinition[]>(defaultOAuthScopeCatalog);

    useEffect(() => {
        let active = true;

        authorizedAxiosInstance
            .get<OAuthScopeDefinition[]>('/api/oauth-applications/scopes')
            .then(({ data }) => {
                if (active && Array.isArray(data) && data.length > 0) {
                    setCatalog(data);
                }
            })
            .catch((error) => captureException(error));

        return () => {
            active = false;
        };
    }, []);

    const scopeKeys = useMemo(() => new Set(catalog.map(({ key }) => key)), [catalog]);

    const handleCheck: TreeProps['onCheck'] = (checkedKeys) => {
        const keys = Array.isArray(checkedKeys) ? checkedKeys : checkedKeys.checked;
        onChange?.(keys.map(String).filter((key) => scopeKeys.has(key)));
    };

    const switcherIcon: TreeProps['switcherIcon'] = ({ expanded }) => (
        <TbChevronDown
            style={{
                transform: `rotate(${expanded ? '0deg' : '-90deg'})`,
                color: token.colorTextTertiary,
            }}
        />
    );

    return (
        <div id='oauth-application-field-scopes' tabIndex={-1}>
            {sections.map((section, index) => (
                <div key={section.category}>
                    {index > 0 && <Divider style={{ margin: '1rem 0' }} />}
                    <div style={{ margin: '0 0.25rem 0.5rem' }}>
                        <Typography.Title level={5} style={{ marginBottom: '0.125rem' }}>
                            {section.title}
                        </Typography.Title>
                        <Typography.Text type='secondary'>{section.description}</Typography.Text>
                    </div>
                    <Tree
                        className='oauth-application-scope-tree'
                        switcherIcon={switcherIcon}
                        showLine
                        checkable
                        onCheck={handleCheck}
                        checkedKeys={value}
                        defaultExpandAll
                        treeData={buildOAuthScopeTree(
                            catalog.filter(({ category }) => category === section.category),
                            {
                                colorBorder: token.colorBorder,
                                colorBgContainer: token.colorBgContainer,
                                colorTextTertiary: token.colorTextTertiary,
                            },
                        )}
                        blockNode
                        selectable={false}
                    />
                </div>
            ))}
        </div>
    );
};

export default OAuthApplicationScopeSelector;
